import React from "react";
import { ArrowUpRight } from "lucide-react";
import {
  LazyMotion,
  domAnimation,
  m,
  useReducedMotion,
} from "framer-motion";
import { Link } from "react-router-dom";
import { targetIndustries } from "./target-industries";

const headerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.04,
    },
  },
};

const headerChild = {
  hidden: { opacity: 0, y: 22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 28 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.07 * i,
      duration: 0.58,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  }),
};

const springTransition = {
  type: "spring" as const,
  stiffness: 360,
  damping: 30,
};

/** 与首页「目标行业」卡片栅格一致，供其他页面在自定义标题下复用 */
export function TargetIndustriesCards() {
  const reduceMotion = useReducedMotion();

  return (
    <ul className="relative mx-auto grid max-w-[1200px] list-none grid-cols-1 items-stretch gap-4 p-0 sm:gap-5 md:grid-cols-6">
        {targetIndustries.map((item, index) => {
          const isWide = item.spanClass.includes("col-span-3");

          return (
            <m.li
              key={item.href}
              custom={index}
              variants={cardVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-8% 0px" }}
              className={`min-h-0 ${item.spanClass} h-full`}
            >
              <Link
                to={item.href}
                aria-label={`${item.title}，进入解决方案`}
                className={`group border-default-100 focus-visible:ring-primary/45 relative isolate flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border text-left outline-none transition-[border-color,box-shadow] duration-500 ease-[cubic-bezier(0.33,1,0.68,1)] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:rounded-3xl hover:border-default-200/55 hover:shadow-[0_36px_90px_-42px_rgba(0,0,0,0.78)] ${isWide ? "min-h-[280px] md:min-h-[320px]" : "min-h-[260px] md:min-h-[300px]"}`}
              >
                <img
                  src={item.imageSrc}
                  alt=""
                  role="presentation"
                  loading="lazy"
                  decoding="async"
                  className="pointer-events-none absolute inset-0 z-0 h-full min-h-full w-full min-w-full max-w-none object-cover object-center transition-[transform,filter] duration-[700ms] ease-[cubic-bezier(0.33,1,0.68,1)] will-change-transform [transform:translateZ(0)] scale-[1.02] grayscale-[18%] brightness-[0.88] contrast-[1.05] saturate-[0.92] group-hover:scale-[1.06] group-hover:grayscale-[0%] group-hover:brightness-[1.1] group-hover:saturate-[1.02] motion-reduce:group-hover:scale-[1.02] motion-reduce:group-hover:brightness-[0.95] motion-reduce:transition-none"
                />

                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 z-[1] bg-[linear-gradient(180deg,rgba(0,0,0,0.2)_0%,transparent_28%,transparent_48%,rgba(0,0,0,0.35)_72%,rgba(0,0,0,0.72)_100%)] opacity-[0.55] ring-1 ring-inset ring-white/[0.06] transition-opacity duration-500 group-hover:opacity-[0.88]"
                />

                <m.div
                  className="relative z-10 flex min-h-full flex-1 flex-col justify-end p-3 sm:p-3 md:p-3"
                  whileHover={reduceMotion ? undefined : { y: -2 }}
                  whileTap={reduceMotion ? undefined : { scale: 0.993 }}
                  transition={springTransition}
                >
                  <div className="border-white/[0.08] bg-black/14 max-w-full rounded-2xl border px-4 py-4 shadow-[0_12px_36px_-20px_rgba(0,0,0,0.5)] backdrop-blur-md supports-[backdrop-filter]:bg-black/10 sm:px-5 sm:py-5">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="text-foreground text-[15px] font-semibold leading-snug tracking-tight sm:text-[17px]">
                        {item.title}
                      </h3>
                      <span
                        aria-hidden
                        className="mt-0.5 shrink-0 text-white opacity-0 transition-[opacity,transform] duration-300 ease-out group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100 group-focus-visible:translate-x-0.5 group-focus-visible:-translate-y-0.5 group-focus-visible:opacity-100 motion-reduce:group-hover:translate-x-0 motion-reduce:group-hover:translate-y-0"
                      >
                        <ArrowUpRight
                          strokeWidth={1.35}
                          className="size-[20px]"
                        />
                      </span>
                    </div>
                    <p className="text-default-600 group-hover:text-default-600 mt-2.5 min-h-[3.3em] text-sm leading-[1.65] transition-colors duration-500 [text-shadow:0_1px_14px_rgba(0,0,0,0.35)] sm:mt-3 sm:min-h-[3.25em] sm:text-[15px] sm:leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </m.div>
              </Link>
            </m.li>
          );
        })}
    </ul>
  );
}

export default function TargetIndustriesSection() {
  return (
    <section
      id="industries"
      className="relative mx-auto w-full max-w-6xl px-6 py-40 sm:py-32 lg:px-8 lg:py-40"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-default-300/35 to-transparent"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-[22%] h-[min(480px,52vh)] w-[min(880px,92vw)] -translate-x-1/2 rounded-full bg-primary/[0.055] blur-[110px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-6 top-1/4 h-px max-w-3xl translate-y-px bg-gradient-to-r from-transparent via-primary/20 to-transparent opacity-40 sm:left-1/2 sm:-translate-x-1/2"
      />

      <LazyMotion features={domAnimation}>
        <m.div
          className="relative mb-12 flex flex-col items-center text-center sm:mb-16"
          variants={headerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-10% 0px -8% 0px" }}
        >
          <m.h2
            variants={headerChild}
            className="max-w-3xl text-foreground text-2xl font-bold tracking-tight sm:text-4xl"
          >
            为你的行业量身打造，开箱即用
          </m.h2>
          <m.p
            variants={headerChild}
            className="text-default-500 mt-3 max-w-2xl text-base leading-relaxed sm:text-lg"
          >
            从医美到跨境电商，3Chat Agent 已在数十个行业的真实场景中验证成交能力
          </m.p>
        </m.div>

        <TargetIndustriesCards />
      </LazyMotion>
    </section>
  );
}
